import React, { useCallback, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { CatalogTemplateRow, VitalElement, WizardState } from './types';
import { detectAmbiguousPricing, suggestResolvingElements } from './generateTemplateCsv';

interface VitalElementsStepProps {
  state: WizardState;
  updateState: (patch: Partial<WizardState>) => void;
  catalogRows: CatalogTemplateRow[];
  isLoading: boolean;
}

interface ElementCard {
  key: VitalElement;
  title: string;
  description: string;
}

const ELEMENT_CARDS: ElementCard[] = [
  { key: 'parentSku', title: 'Parent SKU', description: 'Style-level identifier' },
  { key: 'colorCode', title: 'Color Code', description: 'A unique colorway identifier' },
  { key: 'sizeCode', title: 'Size Code', description: 'A unique size identifier' },
];

const ELEMENT_LABELS: Record<VitalElement, string> = {
  parentSku: 'Parent SKU',
  colorCode: 'Color Code',
  sizeCode: 'Size Code',
};

const VitalElementsStep = ({ state, updateState, catalogRows, isLoading }: VitalElementsStepProps) => {
  const toggleElement = useCallback(
    (element: VitalElement) => {
      const current = state.selectedVitalElements;
      const next = current.includes(element) ?
        current.filter(e => e !== element) :
        [...current, element];
      updateState({ selectedVitalElements: next });
    },
    [state.selectedVitalElements, updateState],
  );

  const selectedCount = state.selectedVitalElements.length;

  const ambiguousEntries = useMemo(
    () => (isLoading || selectedCount === 0) ?
      [] :
      detectAmbiguousPricing(catalogRows, state.selectedVitalElements),
    [catalogRows, state.selectedVitalElements, isLoading, selectedCount],
  );

  const suggestedElements = useMemo(
    () => ambiguousEntries.length > 0 ?
      suggestResolvingElements(catalogRows, state.selectedVitalElements) :
      { elements: [], requiresAll: false },
    [catalogRows, state.selectedVitalElements, ambiguousEntries.length],
  );

  const allElementsSelected = state.selectedVitalElements.length === ELEMENT_CARDS.length;

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 1 }}>
        Which catalog vital elements should prices be set by?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the catalog vital elements below that you&apos;d like to set price overrides by.
        This will inform how pricing is populated in the template based on your selections on the next screen.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {ELEMENT_CARDS.map((card) => {
          const isSelected = state.selectedVitalElements.includes(card.key);
          return (
            <Paper
              key={card.key}
              variant="outlined"
              onClick={() => toggleElement(card.key)}
              sx={{
                flex: 1,
                p: 2,
                cursor: 'pointer',
                borderColor: isSelected ? 'primary.main' : 'divider',
                borderWidth: isSelected ? 2 : 1,
                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': {
                  borderColor: isSelected ? 'primary.main' : 'text.secondary',
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {card.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.description}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Analyzing catalog data...
          </Typography>
        </Box>
      )}

      {!isLoading && ambiguousEntries.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {allElementsSelected && suggestedElements.elements.length === 0 ? (
            <>
              Even with all vital elements selected, some rows map to multiple resale prices.
              Go back and narrow the scope to exclude the affected parent SKUs
              {ambiguousEntries.length <= 5 && (
                <>
                  :
                  {ambiguousEntries.map(e => e.parentSku).join(', ')}
                </>
              )}
              .
            </>
          ) : (
            <>
              The combination of vital elements selected results in more than one distinct resale
              price per row. Please adjust your vital element selections to proceed.
              {suggestedElements.elements.length > 0 && (
                <>
                  {' '}
                  Adding
                  {' '}
                  <strong>
                    {suggestedElements.elements.map(e => ELEMENT_LABELS[e]).join(suggestedElements.requiresAll ? ' and ' : ' or ')}
                  </strong>
                  {' '}
                  would resolve this.
                </>
              )}
            </>
          )}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary">
        {selectedCount === 0 ?
          'No vital elements selected' :
          `${selectedCount} vital element${selectedCount > 1 ? 's' : ''} selected`}
      </Typography>
    </Box>
  );
};

export default VitalElementsStep;
